import { useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { defaultAxiosInstance } from "../api/defaultInstance";
import { useAccess } from "../context/AccessContext";
import { useUser } from "../context/UserContext";
import "../css/Layout.css";
import { SIDEBAR_MENU, canAccessPath } from "../utils/routeAccess";

// 현재 경로가 속한 하위 메뉴 그룹 (새로고침 시 해당 그룹을 펼친다)
const findOpenMenu = (pathname) =>
  SIDEBAR_MENU.find((menu) =>
    menu.items?.some((item) => pathname === item.to || pathname.startsWith(`${item.to}/`)),
  )?.id ?? null;

function Layout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { setUserInfo } = useUser();
  const { hasPermission } = useAccess();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false); // Add new state for mobile menu

  const sidebarRef = useRef(null);
  const isResizingRef = useRef(false);
  const [sidebarWidth, setSidebarWidth] = useState(260);
  const MIN_WIDTH = 200;
  const MAX_WIDTH = 420;
  const COLLAPSED_WIDTH = 40;

  const [openMenu, setOpenMenu] = useState(() => findOpenMenu(location.pathname));

  // 메뉴 노출 = ACCESS_RULES의 requires 권한 보유 여부 (routeAccess.js와 같은 표, R-FE-02)
  const visibleMenu = SIDEBAR_MENU.map((menu) =>
    menu.items
      ? { ...menu, items: menu.items.filter((item) => canAccessPath(item.to, hasPermission)) }
      : menu,
  ).filter((menu) => (menu.items ? menu.items.length > 0 : canAccessPath(menu.to, hasPermission)));

  const toggleMenu = (menuName) => {
    setOpenMenu((prev) => (prev === menuName ? null : menuName));
  };

  const toggleSidebar = () => {
    setIsCollapsed((prev) => !prev);
    // 사이드바가 접힐 때 열려있는 서브메뉴도 닫기
    if (!isCollapsed) {
      setOpenMenu(null);
    }
  };

  const toggleMobileMenu = () => {
    // New function to toggle mobile menu
    setIsMobileMenuOpen((prev) => !prev);
  };

  const handleLogout = async () => {
    try {
      await defaultAxiosInstance.post("/admin/login/logout");
      setUserInfo(null);
      navigate("/", { replace: true });
    } catch (error) {
      console.error("로그아웃 실패:", error);
      // 에러가 발생해도 로그아웃 처리 진행
      setUserInfo(null);
      navigate("/", { replace: true });
    }
  };

  const startResize = (e) => {
    if (isCollapsed) return;
    e.preventDefault();
    isResizingRef.current = true;
    document.body.classList.add("sidebar-resizing");
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizingRef.current) return;
      if (!sidebarRef.current) return;

      const left = sidebarRef.current.getBoundingClientRect().left;
      const newWidth = e.clientX - left;

      const clampedWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, newWidth));

      setSidebarWidth(clampedWidth);
    };

    const handleMouseUp = () => {
      if (!isResizingRef.current) return;
      isResizingRef.current = false;
      document.body.classList.remove("sidebar-resizing");
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      document.body.classList.remove("sidebar-resizing");
    };
  }, []);

  const effectiveWidth = (isCollapsed ? COLLAPSED_WIDTH : sidebarWidth) + 3;

  return (
    <div className="layout-container d-flex">
      {/* Mobile Menu Toggle Button */}
      <button
        className={`mobile-menu-toggle ${isMobileMenuOpen ? "open" : ""}`}
        onClick={toggleMobileMenu}
        aria-label="모바일 메뉴 열기/닫기"
        aria-expanded={isMobileMenuOpen}
      >
        <span className="hamburger-icon">{isMobileMenuOpen ? "✕" : "☰"}</span>
      </button>

      {/* 네비게이션 바 */}
      <nav
        ref={sidebarRef}
        className={`sidebar ${isCollapsed ? "collapsed" : ""} ${isMobileMenuOpen ? "mobile-open" : ""}`}
        style={{ width: effectiveWidth }}
      >
        <div className="sidebar-header">
          <h2 className="logo">
            <span className="logo-line">CATCHRANK</span>
            <span className="logo-line">ADMIN</span>
          </h2>
        </div>
        <ul className="nav flex-column">
          {visibleMenu.map((menu) =>
            menu.items ? (
              <li className="nav-item" key={menu.id}>
                <div
                  className="nav-link submenu-toggle"
                  onClick={() => toggleMenu(menu.id)}
                >
                  {menu.label}
                </div>
                {openMenu === menu.id && (
                  <ul className="submenu">
                    {menu.items.map((item) => (
                      <li key={item.to}>
                        <NavLink
                          to={item.to}
                          end={item.end}
                          className="nav-link"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          {item.label}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ) : (
              <li className="nav-item" key={menu.id}>
                <NavLink
                  to={menu.to}
                  className={({ isActive }) =>
                    isActive ? "nav-link active" : "nav-link"
                  }
                  onClick={() => {
                    setOpenMenu(null);
                    setIsMobileMenuOpen(false);
                  }}
                >
                  {menu.label}
                </NavLink>
              </li>
            ),
          )}

          {/* 로그아웃 */}
          <li className="nav-item">
            <button
              className="nav-link"
              onClick={handleLogout}
              style={{
                border: "none",
                background: "none",
                width: "100%",
                textAlign: "left",
                cursor: "pointer",
                padding: "0.5rem 1rem",
              }}
            >
              로그아웃
            </button>
          </li>
        </ul>

        {/* 드래그 핸들 */}
        <div className="sidebar-resizer" onMouseDown={startResize} />
        {/* 사이드바 토글 버튼 */}
        <button
          className="sidebar-toggle"
          onClick={toggleSidebar}
          aria-label="메뉴 접기/펼치기"
        >
          <span className="hamburger-icon">{isCollapsed ? "»" : "«"}</span>
        </button>
      </nav>

      {/* 메인 콘텐츠 */}
      <div className="main-content">{children}</div>

      {/* 모바일 사이드바 오버레이 */}
      {isMobileMenuOpen && (
        <div
          className="mobile-sidebar-overlay active"
          onClick={toggleMobileMenu}
        ></div>
      )}
    </div>
  );
}

export default Layout;
